import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CofiDeleteFromS3, CofiUploadToS3, CofiViewFromS3 } from "../../util/API/cofi";
import DownloadIcon from "@mui/icons-material/Download";
import CustomAlert from "../UI/CustomAlert/CustomAlert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function CofiFileUploads() {
  const fileInputRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [alertNew, setAlertNew] = useState({
    showAlert: false,
    severity: "",
    header: "",
    message: "",
    onConfirm: null,
    alertType: ''
  });
  const [alert, setAlert] = useState({
    showAlert: false,
    message: "",
    severity: "",
    header: "",
    type: "",
  });

  useEffect(()=> {
    handleGetDocList();
  }, []);

  /* ---------------- handlers ---------------- */

  const handleGetDocList = async() => {
    try {
      const response = await CofiViewFromS3("REQ-20260120-0001", "9c2c9d3f-2e6b-4c8f-9b7e-22c90f9c1234");
      console.log("handleGetDocList_response==========",response);

      const files = transformS3Response(response);
      setUploadedFiles(files);

    } catch(error) {
      console.log("handleGetDocList_error------",error);
    }
  }

  const transformS3Response = (response) => {
    const regions = response?.payload?.header?.regions || [];
  
    return regions.map((region) => ({
      regionName: region.regionName,
      files: (Array.isArray(region.files) ? region.files : [region.files]).map(
        (file) => ({
          id: crypto.randomUUID(),
          file: {
            name: file.fileName,
            s3Key: file.s3Key,
            size: Number(file.fileSize),
            type: file.fileName.endsWith(".pdf")
              ? "application/pdf"
              : "text/plain",
          },
          lastModified: file.lastModified,
        })
      ),
    }));
  };

  const handleSelectFiles = (files) => {
    const mapped = Array.from(files).map((file) => ({
      id: crypto.randomUUID(),
      file
    }));
    setSelectedFiles((prev) => [...prev, ...mapped]);
  };

  const handleFileChange = (e) => {
    if (e.target.files?.length) {
      handleSelectFiles(e.target.files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleSelectFiles(e.dataTransfer.files);
  };

  const handleRemoveSelected = (id) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    console.log("selectedFiles==========",selectedFiles);
    console.log("selectedFilesDetails==========",selectedFiles[0]?.file);
    const base64Content = await fileToBase64(selectedFiles[0].file);
    // 🔥 Later: API call here
    // await uploadFiles(selectedFiles)

    const response = await CofiUploadToS3("REQ-20260120-0001", "9c2c9d3f-2e6b-4c8f-9b7e-22c90f9c1234", selectedFiles[0]?.file?.name, base64Content, "Asia");
    console.log("handleUpload_response==========",response);

    setUploadedFiles((prev) => [...prev, ...selectedFiles]);
    setSelectedFiles([]);
    handleGetDocList();
  };

  const handleDownload = async (file) => {
    console.log("file==========",file);
    // const blob = await CofiDownloadFromS3(file.s3Key);
  
    // const url = window.URL.createObjectURL(blob);
  
    // const a = document.createElement("a");
    // a.href = url;
    // a.download = file.name;
    // a.click();
  
    // URL.revokeObjectURL(url);
  };

  const handleDeleteFile = async(id, s3Key) => {
    try {
      const response = await CofiDeleteFromS3("REQ-20260120-0001", "9c2c9d3f-2e6b-4c8f-9b7e-22c90f9c1234", s3Key);
      console.log("response=========",response);
      if(response?.payload?.header?.callStatus == "1") {
        setAlert({
          showAlert: true,
          message: `File deleted successfully!`,
          severity: "success",
          header: "Congratulations!",
        });
        setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
        handleGetDocList();
      }
    } catch (error) {
      console.log("handleDeleteFileError------------", error);
    }
  }

  /* ---------------- utils ---------------- */

  const getFileIcon = (type) => {
    if (type.includes("pdf")) return <PictureAsPdfIcon color="error" />;
    if (type.includes("word")) return <DescriptionIcon color="primary" />;
    return <InsertDriveFileIcon />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024)
      return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDeleteUploaded = (id, s3Key) => {
    setAlertNew({
      ...alertNew,
      showAlert: true,
      message: "Are you sure you want to delete this file?",
      severity: "confirm",
      header: "Please Confirm",
      alertType: 'confirm',
      onConfirm: () => handleDeleteFile(id, s3Key)
    })
      // 🔥 Later: call backend delete API here
      setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // strip: data:application/pdf;base64,
      resolve(reader.result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  /* ---------------- UI ---------------- */

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        File Uploads
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Upload documents to provide additional context for CoFi.
      </Typography>

      {/* Upload zone */}
      <Paper
        elevation={0}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current.click()}
        sx={{
          border: "2px dashed #cfcfcf",
          borderRadius: 2,
          padding: 4,
          textAlign: "center",
          cursor: "pointer",
          mb: 3,
          backgroundColor: "#fafafa",
          "&:hover": { backgroundColor: "#f5f5f5" }
        }}
      >
        <UploadFileIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
        <Typography>
          Drag & drop files here, or <strong>click to browse</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          PDF, DOCX, TXT
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          onChange={handleFileChange}
        />
      </Paper>

      {/* Selected files */}
      {selectedFiles.length > 0 && (
        <>
          <Typography variant="subtitle1" mb={1}>
            Selected Files
          </Typography>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <List>
              {selectedFiles.map(({ id, file, region, lastModified }) => (
                <ListItem
                  key={id}
                  secondaryAction={
                    <IconButton onClick={() => handleRemoveSelected(id)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>{getFileIcon(file.type)}</ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatSize(file.size)}
                    // secondary={`Region: ${region} • ${formatSize(file.size)} • ${lastModified}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Box display="flex" justifyContent="flex-end" mb={4}>
            <Button
              variant="contained"
              onClick={handleUpload}
            >
              Upload Files
            </Button>
          </Box>
        </>
      )}

      {/* Uploaded files */}
      <Typography variant="subtitle1" mb={1}>
        Uploaded Files
      </Typography>

      {uploadedFiles.length === 0 ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No files uploaded yet.
        </Typography>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            p: 1
          }}
        >
          {uploadedFiles.map((region) => (
            <Box key={region.regionName} mb={2}>
              {/* Region Header */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  background: "#f5f5f5",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  mb: 1
                }}
              >
                {region.regionName}
              </Typography>

              <List dense>
                {region?.files?.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      secondaryAction={
                        <Box>
                          {/* Download later */}
                          {/* <IconButton onClick={() => handleDownload(item.file)}>
                            <DownloadIcon />
                          </IconButton> */}

                          <IconButton
                            onClick={() =>
                              handleDeleteUploaded(item.id, item.file.s3Key)
                            }
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon>{getFileIcon(item.file.type)}</ListItemIcon>

                      <ListItemText
                        primary={item.file.name}
                        secondary={formatSize(item.file.size)}
                      />
                    </ListItem>

                    {index !== region.files.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ))}
        </Paper>
      )}

      <CustomAlert
        severity={alertNew.severity}
        show={alertNew.showAlert}
        header={alertNew.header}
        message={alertNew.message}
        onClick={() => {
            setAlertNew({ ...alertNew, showAlert: false });
        }}
        alertType={alertNew.alertType}
        confirmButton={alertNew.onConfirm}
        autoclose={alertNew.severity === "error" ? false : true}
      />

      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={(prevDetails) =>
            setAlert({ ...prevDetails, showAlert: false })
        }
        alertType={alert.type}
        confirmButton={alert.onConfirm}
        autoclose={alert.severity === "error" ? false : true}
      />
    </Box>
  );
}

export default CofiFileUploads;
